import instantsearch from 'instantsearch.js';
import {
  searchBox,
  configure,
  hits,
  clearRefinements,
  refinementList,
  stats,
  pagination,
} from 'instantsearch.js/es/widgets';
import searchClient from './searchClient';
import SearchPage from './search-page';

const containers = {
  searchbox: '#people-search-searchbox',
  stats: '#people-search-stats',
  hitsContainer: '#people-search-hits-container',
  hits: '#people-search-hits',
  pagination: '#people-search-pagination',
  refinementListType: '#people-search-refinement-list-type',
  refinementListDepartments: '#people-search-refinement-list-department',
  refinementListFacultyTypes: '#people-search-refinement-list-faculty-types',
  refinementListStudentTypes: '#people-search-refinement-list-student-types',
  refinementListStaffTypes: '#people-search-refinement-list-staff-types',
  refinementListFriendsPartnersTypes: '#people-search-refinement-list-friends-partners-types',
  refinementListResearchCenters: '#people-search-refinement-list-research-centers',
  refinementListTopics: '#people-search-refinement-list-topics',
  refinementListDegrees: '#people-search-refinement-list-degrees',
  clearRefinements: '#people-search-clear-refinements',
};

class AlgoliaPeopleSearch extends SearchPage {
  constructor(indexName, department = null, researchCenter = null) {
    super(indexName);

    this.indexName = indexName;

    // Pre-filter parameters
    this.department = department;
    this.departmentFilter = this.department ? ` departments:"${this.department}"` : '';

    this.researchCenter = researchCenter;
    this.researchCenterFilter = this.researchCenter
      ? ` research_centers:"${this.researchCenter}"`
      : '';

    this.preFilter = this.departmentFilter || this.researchCenterFilter;

    this.search = instantsearch({
      indexName: this.indexName,
      routing: true,
      searchClient,
      // https://www.algolia.com/doc/api-reference/widgets/instantsearch/js/#widget-param-searchfunction
      searchFunction: helper => {
        helper.search();
      },
    });

    this.initialize();
    this.search.start();

    // Handle back button route changes
    window.onpopstate = event => {
      // If the input is still focused, change value of input to the current query param
      if (document.activeElement.classList.contains(this.searchInputClassName)) {
        const inputEl = document.querySelector(`.${this.searchInputClassName}`);
        if (event.state) {
          inputEl.value = event.state[this.indexName].query;
        } else {
          inputEl.value = this.queryParam;
        }
      }
    };
  }

  initialize() {
    // https://www.algolia.com/doc/api-reference/widgets/js/
    this.search.addWidgets([
      searchBox({
        container: containers.searchbox,
        placeholder: 'Search people',
        showSubmit: false,
        cssClasses: {
          input: this.searchInputClassName,
        },
        templates: {
          reset: `
            <svg class="search__reset">
              <use xlink:href="#icon-x"></use>
            </svg>
          `,
        },
      }),

      // https://www.algolia.com/doc/api-reference/widgets/configure/js/
      // Parameter options: https://www.algolia.com/doc/api-reference/search-api-parameters/
      configure({
        hitsPerPage: this.hitsPerPage,
        distinct: true,
        // Force faceting to be applied after de-duplication (via the Distinct setting)
        facetingAfterDistinct: true,
        // Filter the query with pre-selected department or research center
        // https://www.algolia.com/doc/api-reference/api-parameters/filters/#facet-filters
        filters: this.preFilter,
      }),

      // https://www.algolia.com/doc/api-reference/widgets/stats/js/
      stats({
        container: containers.stats,
        templates: {
          text: data => {
            let count = '';

            const pagination = document.querySelector(containers.pagination);
            this.toggleHide(pagination, data.nbHits <= this.hitsPerPage);

            if (data.hasManyResults) {
              count += `${data.nbHits} results found`;
            } else if (data.hasOneResult) {
              count += `1 result found`;
            } else {
              count += `<span class="search__stats-no-results">No results found.</span>`;
            }

            return `${count}`;
          },
        },
      }),

      // https://www.algolia.com/doc/api-reference/widgets/hits/js/
      hits({
        container: containers.hits,
        escapeHTML: false,
        templates: {
          /* eslint-disable */
          item(hit) {
            return `
              <article>
                <div class="hit-left">
                  <a href="${hit.url}">
                    ${
                      hit.headshot
                        ? `
                      <div class="hit-image-wrapper">
                        <img class="" src="${hit.headshot}" alt="${hit.title}" />
                      </div>
                      `
                        : `
                      <div class="hit-image-wrapper-placeholder">
                        <svg>
                          <use xlink:href="#ne-n"></use>
                        </svg>
                      </div>
                    `
                    }
                    <div class="hit-title">
                      <strong>
                        ${instantsearch.highlight({ attribute: 'title', hit })}
                      </strong>
                      ${hit.job_title ? `<p>${hit.job_title}</p>` : ``}
                      ${hit.areas_of_study ? `<p>${hit.areas_of_study}</p>` : ``}
                    </div>

                    ${
                      hit.pathway
                        ? `
                        <div class="hit-pathway">
                          <p class="hit-pathway-text">See Path</p>
                          <svg class="hit-pathway-icon">
                            <use xlink:href="#ela-icon"></use>
                          </svg>
                        </div>
                        `
                        : ``
                    }
                  </a>
                </div>

                ${
                  !hit.areas_of_study
                    ? `
                  <div class="hit-right">
                    ${
                      hit.phone_number
                        ? `
                    <div class="hit-info-item">
                      <a href="tel:${hit.phone_number}" class="hit-info-text">
                        <span>${hit.phone_number}</span>
                      </a>
                    </div>
                    `
                        : `
                    <div class="hit-info-item">
                      <p class="hit-info-text disabled">No number listed</p>
                    </div>
                    `
                    }

                    ${
                      hit.email_address
                        ? `
                      <div class="hit-info-item">
                        <a href="mailto:${hit.email_address}" class="hit-info-text">
                          <span>${hit.email_address}</span>
                        </a>
                      </div>
                      `
                        : `
                      <div class="hit-info-item">
                        <p class="hit-info-text disabled">No email listed</p>
                      </div>
                    `
                    }
                  </div>
                `
                    : ``
                }
              </article>
            `;
          },
          /* eslint-enable */
        },
        transformItems: items => {
          // Hide pagination and results if no results
          this.toggleHide(this.results, items.length < 1);
          if (window.innerWidth <= 768 && items.length > 0) {
            this.toastActive();
          }
          return items;
        },
      }),

      // https://www.algolia.com/doc/api-reference/widgets/pagination/js/
      pagination({
        container: containers.pagination,
        showFirst: false,
        showLast: false,
        scrollTo: 'main',
        templates: {
          previous: `
            <span class="visually-hidden">Previous page</span>
            <svg class="arrow prev">
              <use xlink:href="#icon-arrow"></use>
            </svg>
          `,
          next: `
            <span class="visually-hidden">Next page</span>
            <svg class="arrow next">
              <use xlink:href="#icon-arrow"></use>
            </svg>
          `,
        },
      }),

      // Faculty types
      refinementList({
        container: containers.refinementListFacultyTypes,
        attribute: 'faculty_types',
        operator: 'and',
        limit: 100, // set to arbitrary large number to prevent default limit of 10
        sortBy: this.alphaSortRefinements,
        transformItems: items => {
          // Hide refinement list if there are no results
          const refList = document
            .querySelector(containers.refinementListFacultyTypes)
            .closest('.js--accordion');

          this.updateRefListClasses(items, refList);

          this.toggleHide(refList, items.length < 1);
          return items;
        },
      }),

      // Student types
      refinementList({
        container: containers.refinementListStudentTypes,
        attribute: 'student_types',
        operator: 'and',
        limit: 100, // set to arbitrary large number to prevent default limit of 10
        sortBy: this.alphaSortRefinements,
        transformItems: items => {
          // Hide refinement list if there are no results
          const refList = document
            .querySelector(containers.refinementListStudentTypes)
            .closest('.js--accordion');

          this.updateRefListClasses(items, refList);

          this.toggleHide(refList, items.length < 1);
          return items;
        },
      }),

      // Staff types
      refinementList({
        container: containers.refinementListStaffTypes,
        attribute: 'staff_types',
        operator: 'and',
        limit: 100, // set to arbitrary large number to prevent default limit of 10
        sortBy: this.alphaSortRefinements,
        transformItems: items => {
          // Hide refinement list if there are no results
          const refList = document
            .querySelector(containers.refinementListStaffTypes)
            .closest('.js--accordion');

          this.updateRefListClasses(items, refList);

          this.toggleHide(refList, items.length < 1);
          return items;
        },
      }),

      // Friends & Partners types
      refinementList({
        container: containers.refinementListFriendsPartnersTypes,
        attribute: 'friends_partners_types',
        operator: 'and',
        limit: 100, // set to arbitrary large number to prevent default limit of 10
        sortBy: this.alphaSortRefinements,
        transformItems: items => {
          // Hide refinement list if there are no results
          const refList = document
            .querySelector(containers.refinementListFriendsPartnersTypes)
            .closest('.js--accordion');

          this.updateRefListClasses(items, refList);

          this.toggleHide(refList, items.length < 1);
          return items;
        },
      }),

      // School or Department
      refinementList({
        container: containers.refinementListDepartments,
        attribute: 'departments',
        operator: 'and',
        limit: 100, // set to arbitrary large number to prevent default limit of 10
        sortBy: this.alphaSortRefinements,
        transformItems: items => {
          // Hide refinement list if there are no results or if department is specified
          const refList = document
            .querySelector(containers.refinementListDepartments)
            .closest('.js--accordion');

          this.updateRefListClasses(items, refList);

          this.toggleHide(refList, this.department || items.length < 1);
          return items;
        },
      }),

      // Degree
      refinementList({
        container: containers.refinementListDegrees,
        attribute: 'degrees',
        operator: 'and',
        limit: 100, // set to arbitrary large number to prevent default limit of 10
        sortBy: this.alphaSortRefinements,
        transformItems: items => {
          // Hide refinement list if there are no results or if department is specified
          const refList = document
            .querySelector(containers.refinementListDegrees)
            .closest('.js--accordion');

          this.updateRefListClasses(items, refList);

          this.toggleHide(refList, this.department || items.length < 1);
          return items;
        },
      }),

      // Research Centers
      refinementList({
        container: containers.refinementListResearchCenters,
        attribute: 'research_centers',
        operator: 'and',
        limit: 100, // set to arbitrary large number to prevent default limit of 10
        sortBy: this.alphaSortRefinements,
        transformItems: items => {
          // Hide refinement list if there are no results or if research center is specified
          const refList = document
            .querySelector(containers.refinementListResearchCenters)
            .closest('.js--accordion');

          this.updateRefListClasses(items, refList);

          this.toggleHide(refList, this.researchCenter || items.length < 1);
          return items;
        },
      }),

      // Topics
      refinementList({
        container: containers.refinementListTopics,
        attribute: 'topics',
        operator: 'and',
        limit: 100, // set to arbitrary large number to prevent default limit of 10
        sortBy: this.alphaSortRefinements,
        transformItems: items => {
          // Hide refinement list if there are no results
          const refList = document
            .querySelector(containers.refinementListTopics)
            .closest('.js--accordion');

          this.updateRefListClasses(items, refList);

          this.toggleHide(refList, items.length < 1);
          return items;
        },
      }),

      // https://www.algolia.com/doc/api-reference/widgets/clear-refinements/js/
      clearRefinements({
        container: containers.clearRefinements,
        templates: {
          resetLabel: 'Clear filters',
        },
      }),
    ]);
  }

  static init() {
    new AlgoliaPeopleSearch();
  }
}

export default AlgoliaPeopleSearch;
